import React, { useRef, useState, useEffect, useMemo } from 'react';
import { algoliasearch } from 'algoliasearch';
import { InstantSearch, Configure, useHits, useInstantSearch } from 'react-instantsearch';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
    Button,
    IconCollection,
    LayoutSingleColumn,
    Page,
    DashboardTabBar,
} from '../../components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import EventProviderDashboardPageForm from './EventProviderDashboardPageForm';
import { parse, stringify } from '../../util/urlHelpers';
import { getProviderCompletedTransactions, getProviderEventDashboardTransactions } from '../../util/api';
import { makeOffer } from '../MakeOfferPage/MakeOfferPage.duck';
import { makeTransitionNormal, fetchCommission } from '../TransactionPage/TransactionPage.duck';

import css from './EventProviderDashboardPage.module.css';

const searchClient = algoliasearch(
    process.env.REACT_APP_ALGOLIA_APP_ID,
    process.env.REACT_APP_ALGOLIA_API_KEY,
    {
        _useRequestCache: true,
    }
);

// Invoice states that represent money the provider has actually earned (offer
// agreed or event delivered/completed/reviewed). Mirrors the green "settled"
// states used by the Invoices tab.
const EARNED_INVOICE_STATES = new Set([
    'state/offer-accepted',
    'state/delivered',
    'state/completed',
    'state/reviewed',
    'state/reviewed-by-customer',
    'state/reviewed-by-provider',
]);

const resolveInvoiceType = tx => {
    const pd = tx?.attributes?.protectedData || {};
    if (pd?.invoiceData?.invoiceType) return pd.invoiceData.invoiceType;
    if (pd?.isOvertimeInvoice) return 'overtime';
    if (pd?.isExpenseInvoice) return 'expense';
    if (pd?.isReductionInvoice) return 'reduction';
    return 'invoice';
};

const toCents = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// Net a gross amount by a commission percentage. Defensive against a negative
// stored percentage (line-item configs store the provider commission negated).
const netOfCommission = (grossCents, pct) => {
    const rate = Math.abs(Number(pct) || 0) / 100;
    return Math.round(grossCents * (1 - rate));
};

// Provider commission % for a transaction item: prefer the admin override
// captured on the bid, otherwise the marketplace-wide percentage.
const resolveProviderCommissionPct = (item, marketplacePct) => {
    const override = Number(item?.protectedData?.finalBid?.adminProviderCommissionMaybe);
    return Number.isFinite(override) && override !== 0 ? override : marketplacePct;
};

// Total provider earnings (in cents), net of provider commission: base amount
// from every completed job plus any settled overtime / expense invoice.
const computeProviderNetEarnings = ({ completed, invoices, marketplaceProviderPct }) => {
    const baseCents = (Array.isArray(completed) ? completed : []).reduce((sum, item) => {
        const gross = toCents(item?.protectedData?.finalBid?.bidAmount);
        return sum + netOfCommission(gross, resolveProviderCommissionPct(item, marketplaceProviderPct));
    }, 0);

    const invoiceCents = (Array.isArray(invoices) ? invoices : []).reduce((sum, item) => {
        const tx = item?.transaction;
        const state = tx?.attributes?.state;
        const type = resolveInvoiceType(tx);
        const isEarnedExtra =
            (type === 'overtime' || type === 'expense') && EARNED_INVOICE_STATES.has(state);
        if (!isEarnedExtra) return sum;
        const pd = item?.protectedData || {};
        const gross = toCents(pd?.invoiceData?.totalAmount ?? pd?.totalAmount);
        return sum + netOfCommission(gross, resolveProviderCommissionPct(item, marketplaceProviderPct));
    }, 0);

    return baseCents + invoiceCents;
};

const mapAlgoliaHitToDashboardEvent = hit => {
    const firstDetail = Array.isArray(hit.eventDetails) ? hit.eventDetails[0] : {};
    const { date, photoBoothStartTime } = firstDetail || {};

    return {
        id: hit.objectID || '',
        title: hit.eventTitle || hit.title || '',
        type: hit.eventType || '',
        client: hit?.user?.displayName || hit?.user?.firstName || "",
        date: date || (Array.isArray(hit.eventDates) ? hit.eventDates[0] : ''),
        time: photoBoothStartTime || '',
        status: ' ',
        statusType: ' ',
        invoiceAmount: '$0',
        adjustment: '',
        // actionButton: 'View Event',
        actionType: 'view',
        rawHit: hit,
        eventStartDateUnix: hit.eventStartDateUnix,
        eventBid: hit.eventBid,
    };
};

const AlgoliaEventsSync = props => {
    const { onEventsChange, onTotalChange } = props;
    const { hits, results } = useHits();

    const nbHits = typeof results?.nbHits === 'number' ? results.nbHits : hits.length;
    const page = typeof results?.page === 'number' ? results.page : 0;
    const query = typeof results?.query === 'string' ? results.query : '';

    const lastSyncRef = useRef({ nbHits: null, page: null, query: null, hitsLen: null });

    useEffect(() => {
        const nextKey = { nbHits, page, query, hitsLen: hits.length };
        const prevKey = lastSyncRef.current;

        const hasChanged =
            prevKey.nbHits !== nextKey.nbHits ||
            prevKey.page !== nextKey.page ||
            prevKey.query !== nextKey.query ||
            prevKey.hitsLen !== nextKey.hitsLen;

        if (!hasChanged) {
            return;
        }

        lastSyncRef.current = nextKey;

        const mappedEvents = Array.isArray(hits) ? hits.map(mapAlgoliaHitToDashboardEvent) : [];
        onEventsChange(mappedEvents);
        onTotalChange(nbHits);
    }, [hits, nbHits, page, query, onEventsChange, onTotalChange]);

    return null;
};

const AlgoliaSearchStatusSync = props => {
    const { onSearchingChange } = props;
    const { status } = useInstantSearch();

    useEffect(() => {
        const isSearching = status === 'loading' || status === 'stalled';
        onSearchingChange(isSearching);
    }, [status, onSearchingChange]);

    return null;
};

const EventProviderDashboardPageComponent = props => {
    const {
        scrollingDisabled = false,
        intl,
        config,
        routes,
        history,
        currentUser,
        initiatedTransactions,
        onManageDisableScrolling,
        onMakeOffer,
        location: propLocation,
        eventTypes,
        onMakeTransitionNormal,
        commission,
        onFetchCommission,
    } = props;

    const userPublicData = currentUser?.attributes?.profile?.publicData;
    const offeredServices = userPublicData?.offeredServices;
    const boothTypeSubcategories = offeredServices?.flatMap(service => service.subCategories) ?? [];
    const address1 = userPublicData?.address1;
    const zipCodeRadius = userPublicData?.zipCodeRadius?.[0];

    const origin = address1?.selectedPlace?.origin;

    // Get current user ID to exclude own listings
    const currentUserId = currentUser?.id?.uuid;

    // Service type filter based on offeredServices (old behaviour)
    // Extract service IDs from offeredServices for filtering
    // const offeredServiceIds = offeredServices?.map(service => service.id) || [];
    // const serviceTypeFilter = offeredServiceIds.length > 0
    //     ? `(${offeredServiceIds.map(id => `boothTypes:${id}`).join(' OR ')})`
    // : '';

    // Service type filter based on offeredServices subcategories (new behaviour)
    const serviceTypeFilter = boothTypeSubcategories.length > 0
        ? `(${boothTypeSubcategories.map(id => `boothTypeSubcategories:${id}`).join(' OR ')})`
        : '';

    // Filter to exclude own listings
    const ownListingFilter = currentUserId
        ? `NOT user.id:"${currentUserId}"`
        : '';

    const ITEMS_PER_PAGE = 10;

    const location = history.location || {};
    const queryParams = parse(location.search || '');
    const currentPage = queryParams.page && Number(queryParams.page) > 0 ? Number(queryParams.page) : 1;

    const initialTab = (() => {
        const t = typeof queryParams.tab === 'string' ? queryParams.tab.toLowerCase() : '';
        if (t === 'event' || t === 'events') return 'Events';
        if (t === 'completed-events') return 'CompletedEvents';
        return 'Dashboard';
    })();

    const [activeTab, setActiveTab] = useState(initialTab);
    const [events, setEvents] = useState([]);
    const [totalEvents, setTotalEvents] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Raw transaction sources for the Total Earnings card: completed jobs plus
    // any overtime / expense invoices raised through the invoicing UX.
    const [earningsSources, setEarningsSources] = useState({ completed: [], invoices: [] });

    useEffect(() => {
        const t = typeof queryParams.tab === 'string' ? queryParams.tab.toLowerCase() : '';
        let nextTab = 'Dashboard';
        if (t === 'event' || t === 'events') nextTab = 'Events';
        else if (t === 'completed-events') nextTab = 'CompletedEvents';
        if (nextTab !== activeTab) {
            setActiveTab(nextTab);
        }
    }, [location.search]);

    // Load the raw sources for the Total Earnings card (and the marketplace
    // commission config used to net it down). Summation happens in a memo below
    // so the total recomputes cleanly once the commission asset arrives.
    useEffect(() => {
        if (!currentUserId) return undefined;
        let cancelled = false;

        onFetchCommission();

        // Fetch every page so the total isn't capped at a single page of results.
        // De-duplicate by transactionId: the invoice endpoint re-includes
        // customer-side invoices on every page, which would otherwise be counted
        // more than once.
        const fetchAllTransactions = async fetcher => {
            const byId = new Map();
            const MAX_PAGES = 20;
            let page = 1;
            let totalPages = 1;
            do {
                const res = await fetcher(page);
                const txs = res?.data?.transactions || [];
                txs.forEach((tx, idx) => {
                    const key = tx?.transactionId || `${page}-${idx}`;
                    if (!byId.has(key)) byId.set(key, tx);
                });
                const meta = res?.data?.meta || {};
                totalPages = typeof meta.totalPages === 'number' && meta.totalPages > 0 ? meta.totalPages : 1;
                page += 1;
            } while (page <= totalPages && page <= MAX_PAGES);
            return Array.from(byId.values());
        };

        const loadEarningsSources = async () => {
            try {
                const [completed, invoices] = await Promise.all([
                    fetchAllTransactions(page =>
                        getProviderCompletedTransactions({ userId: currentUserId, page, perPage: 100 })
                    ),
                    fetchAllTransactions(page =>
                        getProviderEventDashboardTransactions({ userId: currentUserId, page, perPage: 100 })
                    ),
                ]);

                if (!cancelled) setEarningsSources({ completed, invoices });
            } catch (e) {
                console.error('Failed to load provider earnings sources', e);
                if (!cancelled) setEarningsSources({ completed: [], invoices: [] });
            }
        };

        loadEarningsSources();
        return () => {
            cancelled = true;
        };
    }, [currentUserId, onFetchCommission]);

    // Marketplace-wide provider commission percentage from the commission asset.
    const marketplaceProviderPct = useMemo(() => {
        const hasData = Array.isArray(commission?.data) && commission.data.length > 0;
        return hasData ? commission.data[0]?.attributes?.data?.providerCommission?.percentage || 0 : 0;
    }, [commission]);

    // Total earnings (in cents), net of provider commission.
    const totalEarnings = useMemo(
        () =>
            computeProviderNetEarnings({
                completed: earningsSources.completed,
                invoices: earningsSources.invoices,
                marketplaceProviderPct,
            }),
        [earningsSources, marketplaceProviderPct]
    );

    const updateUrlTabParam = nextTab => {
        const nextParams = { ...queryParams };
        if (nextTab === 'Events') {
            nextParams.tab = 'event';
        } else if (nextTab === 'CompletedEvents') {
            nextParams.tab = 'completed-events';
        } else {
            delete nextParams.tab;
            delete nextParams.page;
        }
        history.push({ search: `?${stringify(nextParams)}` });
    };

    // zipCodeRadius options 
    //    <option value="80467">50 Miles</option>
    //   <option value="120700">75 Miles</option>
    //   <option value="160930">100 Miles</option>
    //   <option value="201160">125 Miles</option>
    //   <option value="241400">150 Miles</option>
    const defaultServiceRadius = 160930;
    const serviceRadius = zipCodeRadius?.serviceRadius || defaultServiceRadius

    const handlePageChange = page => {
        const nextPage = page && page > 0 ? page : 1;
        const nextParams = { ...queryParams, page: nextPage };
        history.push({ search: `?${stringify(nextParams)}` });
    };

    const handleExportData = () => {
        try {
            const headers = ['EVENT DETAILS', 'CLIENT', 'DATE & TIME', 'STATUS', 'AMOUNT'];
            const csvRows = [headers.join(',')];

            events.forEach(event => {
                const eventDetails = `"${event.title}\n${event.type}\n${event.id}"`;
                const client = event.client;
                const dateTime = `"${event.date}\n${event.time}"`;
                const status = event.status;
                const amount = event.invoiceAmount;

                const row = [eventDetails, client, dateTime, status, amount].join(',');
                csvRows.push(row);
            });

            const csvString = csvRows.join('\n');
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', 'event_data.csv');
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error exporting data:', error);
        }
    };

    // Rounding current time to the start of the current minute to avoid frequent re-renders/requests
    const nowSeconds = useMemo(() => Math.floor(Date.now() / 1000), []);

    const title = intl.formatMessage({ id: 'EventDashboardPage.title' });

    const completedEvents = Array.isArray(events)
        ? events.filter(e => e.statusType === 'completed').length
        : 0;
    const pendingEvents = 0

    const disputedEvents = 0

    const indexName = process.env.REACT_APP_ALGOLIA_EVENT_LISTING_INDEX;

    const hasValidOrigin =
        origin && typeof origin.lat === 'number' && typeof origin.lng === 'number';

    const canUseInstantSearch =
        !!indexName &&
        hasValidOrigin &&
        !!process.env.REACT_APP_ALGOLIA_APP_ID &&
        !!process.env.REACT_APP_ALGOLIA_API_KEY;

    return (
        <Page title={title} scrollingDisabled={scrollingDisabled}>
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<FooterContainer />}
            >
                <div className={css.eventDashboardContainer}>
                    <div className={css.eventDashboardHeader}>
                        <div>
                            <div className={css.eventDashboardHeaderTitle}>
                                <h1 className={css.eventDashboardHeaderTitleH1}>
                                    Service Provider Dashboard
                                </h1>
                                <p className={css.eventDashboardHeaderTitleP}>
                                    Manage your events and overtime requests
                                </p>
                            </div>
                        </div>
                        <div className={css.eventDashboardHeaderButton}>
                            <Button className={css.exportDataButton} onClick={() => { handleExportData() }}>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.4138 18.1243L16.0005 26.6665L24.5871 18.1243C24.6884 18.0471 24.772 17.9491 24.8321 17.8369C24.8923 17.7246 24.9276 17.6008 24.9357 17.4737C24.9438 17.3466 24.9245 17.2193 24.8791 17.1003C24.8337 16.9813 24.7633 16.8735 24.6726 16.7841C24.582 16.6947 24.4731 16.6258 24.3535 16.5821C24.2339 16.5385 24.1063 16.521 23.9794 16.5309C23.8524 16.5408 23.7291 16.5779 23.6177 16.6397C23.5063 16.7014 23.4095 16.7863 23.3338 16.8887L16.8894 23.2798V6.27539C16.8894 6.03965 16.7957 5.81355 16.629 5.64686C16.4623 5.48016 16.2362 5.3865 16.0005 5.3865C15.7647 5.3865 15.5386 5.48016 15.3719 5.64686C15.2052 5.81355 15.1116 6.03965 15.1116 6.27539L15.1116 23.2798L8.66714 16.8887C8.49976 16.7225 8.27321 16.6296 8.03733 16.6305C7.80145 16.6313 7.57556 16.7258 7.40936 16.8932C7.24316 17.0606 7.15025 17.2871 7.15109 17.523C7.15192 17.7589 7.24642 17.9847 7.4138 18.151V18.1243Z" fill="#4A4A4A" />
                                </svg>

                                Export Data
                            </Button>
                            <Button className={css.modifyBillButton} onClick={() => setIsModalOpen(true)}>
                                Create Invoice
                            </Button>
                        </div>
                    </div>
                    {/* Short single-word labels, so the row is kept from tablet width up
                        and collapses to a dropdown only on phones. */}
                    <DashboardTabBar
                      ariaLabel="Dashboard section"
                      selectedTabId={activeTab}
                      tabs={[
                        {
                          id: 'Dashboard',
                          label: 'Dashboard',
                          onSelect: () => {
                            setActiveTab('Dashboard');
                            updateUrlTabParam('Dashboard');
                          },
                        },
                        {
                          id: 'Events',
                          label: 'Events',
                          onSelect: () => {
                            setActiveTab('Events');
                            updateUrlTabParam('Events');
                          },
                        },
                        {
                          id: 'CompletedEvents',
                          label: 'Completed',
                          onSelect: () => {
                            setActiveTab('CompletedEvents');
                            updateUrlTabParam('CompletedEvents');
                          },
                        },
                        {
                          id: 'RPFS',
                          label: 'RPFS',
                          onSelect: () => {
                            setActiveTab('RPFS');
                          },
                        },
                        {
                          id: 'Invoices',
                          label: 'Invoices',
                          onSelect: () => {
                            setActiveTab('Invoices');
                          },
                        },
                      ]}
                    />

                    {/* Total Events    Available Matches    Overtime Claims    Total Earnings           */}
                    {
                        activeTab == 'Dashboard' ? (
                            <div className={css.eventDashboardCardsContainer}>
                                <div className={css.eventDashboardCard}>
                                    <div className={css.eventDashboardCardIcon}>
                                        <IconCollection icon={'calendarIcon'} />
                                    </div>
                                    <div className={css.eventDashboardCardContent}>
                                        <div className={css.eventDashboardCardContentTitle}>
                                            <span>Total Events</span>
                                        </div>
                                        <div className={css.eventDashboardCardContentValue}>
                                            <span>{totalEvents}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={css.eventDashboardCard}>
                                    <div className={css.eventDashboardCardIcon}>
                                        <IconCollection icon={'checkIcon'} />
                                    </div>
                                    <div className={css.eventDashboardCardContent}>
                                        <div className={css.eventDashboardCardContentTitle}>
                                            <span>Available Matches</span>
                                        </div>
                                        <div className={css.eventDashboardCardContentValue}>
                                            <span>{totalEvents}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={css.eventDashboardCard}>
                                    <div className={css.eventDashboardCardIcon}>
                                        <IconCollection icon={'editIcon'} />
                                    </div>
                                    <div className={css.eventDashboardCardContent}>
                                        <div className={css.eventDashboardCardContentTitle}>
                                            <span>Overtime Claims</span>
                                        </div>
                                        <div className={css.eventDashboardCardContentValue}>
                                            <span>0</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={css.eventDashboardCard}>
                                    <div className={css.eventDashboardCardIcon}>
                                        <IconCollection icon={'moneyIcon'} />
                                    </div>
                                    <div className={css.eventDashboardCardContent}>
                                        <div className={css.eventDashboardCardContentTitle}>
                                            <span>Total Earnings</span>
                                        </div>
                                        <div className={css.eventDashboardCardContentValue}>
                                            <span>
                                                {`$${(totalEarnings / 100).toLocaleString('en-US', {
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 2,
                                                })}`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            null
                        )
                    }
                    <div>
                        {canUseInstantSearch ? (
                            <InstantSearch searchClient={searchClient} indexName={indexName}>
                                <Configure
                                    hitsPerPage={ITEMS_PER_PAGE}
                                    page={currentPage && currentPage > 0 ? currentPage - 1 : 0}
                                    aroundLatLng={hasValidOrigin ? `${origin.lat}, ${origin.lng}` : undefined}
                                    aroundRadius={hasValidOrigin ? Number(serviceRadius) : undefined}
                                    filters={[
                                        serviceTypeFilter,
                                        ownListingFilter,
                                        'state:"published"',
                                        `eventEndDateUnix >= ${nowSeconds}`,
                                    ].filter(Boolean).join(' AND ')}
                                />
                                <AlgoliaSearchStatusSync onSearchingChange={setIsSearching} />
                                <AlgoliaEventsSync onEventsChange={setEvents} onTotalChange={setTotalEvents} />
                                <EventProviderDashboardPageForm
                                    config={config}
                                    routes={routes}
                                    currentUser={currentUser}
                                    history={history}
                                    location={propLocation}
                                    onMakeOffer={onMakeOffer}
                                    onSubmitCallback={() => { }}
                                    activeTab={activeTab}
                                    events={events}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    onMakeTransitionNormal={onMakeTransitionNormal}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    totalItems={totalEvents}
                                    useInstantSearch={true}
                                    isSearching={isSearching}
                                    intl={intl}
                                    currentUserId={currentUserId}
                                    initiatedTransactions={initiatedTransactions}
                                    onManageDisableScrolling={onManageDisableScrolling}
                                    isModalOpen={isModalOpen}
                                    setIsModalOpen={setIsModalOpen}
                                    eventTypes={eventTypes}
                                />
                            </InstantSearch>
                        ) : (
                            <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* <IconSpinner /> */}
                                <span>Location is required to load events.</span>
                            </div>
                        )}
                    </div>
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};

const mapStateToProps = state => {
    const { currentUser, eventTypes } = state.user;
    const initiatedTransactions = currentUser?.attributes.profile.protectedData.initiatedTransactions
    return {
        currentUser,
        initiatedTransactions,
        scrollingDisabled: isScrollingDisabled(state),
        eventTypes,
        commission: state.TransactionPage?.commission || null,
    };
};

const mapDispatchToProps = dispatch => ({
    onManageDisableScrolling: (componentId, disableScrolling) => dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onMakeOffer: (params, processAlias, transactionId, isPrivileged) =>
        dispatch(makeOffer(params, processAlias, transactionId, isPrivileged)),
    onMakeTransitionNormal: (transactionId, transitionName, params) =>
        dispatch(makeTransitionNormal(transactionId, transitionName, params)),
    onFetchCommission: () => dispatch(fetchCommission()),
});

const EventProviderDashboardPageEnhancedCompoenet = props => {
    const config = useConfiguration();
    const routes = useRouteConfiguration();
    const history = useHistory();
    const intl = useIntl();

    return (
        <EventProviderDashboardPageComponent
            {...props}
            config={config}
            routes={routes}
            history={history}
            intl={intl}
        />
    );
};

const EventProviderDashboardPage = compose(
    connect(mapStateToProps, mapDispatchToProps),
)(EventProviderDashboardPageEnhancedCompoenet);

export default EventProviderDashboardPage;